import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { NotificacionesData, ApiResponse } from '../types';
import { getAuthToken } from './AuthContext';

interface NotificationContextType {
    items: NotificacionesData['items'];
    noLeidas: number;
    loading: boolean;
    fetchNotificaciones: (soloNoLeidas?: boolean) => Promise<void>;
    marcarLeida: (id: number) => Promise<boolean>;
    marcarTodasLeidas: () => Promise<boolean>;
    delete: (id: number) => Promise<boolean>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const authFetch = async (path: string, options: RequestInit = {}): Promise<Response> => {
    const token = getAuthToken();
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...((options.headers as Record<string, string>) || {}),
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(`${API_BASE}${path}`, { ...options, headers });
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<NotificacionesData['items']>([]);
    const [noLeidas, setNoLeidas] = useState(0);
    const [loading, setLoading] = useState(false);

    const fetchNotificaciones = useCallback(async (soloNoLeidas = false) => {
        setLoading(true);
        try {
            const res = await authFetch(`/notificaciones${soloNoLeidas ? '?no_leidas=true' : ''}`);
            const json: ApiResponse<NotificacionesData> = await res.json();
            if (json.success && json.data) {
                setItems(json.data.items);
                setNoLeidas(json.data.no_leidas);
            }
        } catch (e) {
            console.error('Error al cargar notificaciones:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    const marcarLeida = useCallback(async (id: number): Promise<boolean> => {
        try {
            const res = await authFetch(`/notificaciones/${id}/leida`, { method: 'PATCH' });
            const json: ApiResponse<any> = await res.json();
            if (json.success) {
                setItems(prev => prev.map(n => n.id === id ? { ...n, leida: true } : n));
                setNoLeidas(prev => Math.max(0, prev - 1));
                return true;
            }
            return false;
        } catch {
            return false;
        }
    }, []);

    const marcarTodasLeidas = useCallback(async (): Promise<boolean> => {
        try {
            const res = await authFetch('/notificaciones/leidas-todas', { method: 'PATCH' });
            const json: ApiResponse<any> = await res.json();
            if (json.success) {
                setItems(prev => prev.map(n => ({ ...n, leida: true })));
                setNoLeidas(0);
                return true;
            }
            return false;
        } catch {
            return false;
        }
    }, []);

    const deleteNotif = useCallback(async (id: number): Promise<boolean> => {
        try {
            const res = await authFetch(`/notificaciones/${id}`, { method: 'DELETE' });
            const json: ApiResponse<any> = await res.json();
            if (json.success) {
                setItems(prev => prev.filter(n => n.id !== id));
                return true;
            }
            return false;
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        const token = getAuthToken();
        if (token) {
            fetchNotificaciones();
            const interval = setInterval(() => fetchNotificaciones(), 60000);
            return () => clearInterval(interval);
        }
    }, [fetchNotificaciones]);

    return (
        <NotificationContext.Provider value={{
            items,
            noLeidas,
            loading,
            fetchNotificaciones,
            marcarLeida,
            marcarTodasLeidas,
            delete: deleteNotif,
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = (): NotificationContextType => {
    const ctx = useContext(NotificationContext);
    if (!ctx) throw new Error('useNotifications debe usarse dentro de NotificationProvider');
    return ctx;
};